/**
 * Responsibility: one row per grading metric - what it outputs, how well, how far along.
 *
 * **Accuracy and completeness are two columns because they are two
 * questions.** A metric can agree with the grader on the handful of cards
 * it has been scored on and still be a long way from usable, and one
 * percentage would call that finished.
 *
 * **The percentage is a rubric, not an opinion.** Five equal steps, three
 * of them decided by the corpus: a measurement exists, ground truth is
 * joined, it has been scored on enough cards, it agrees often enough, and
 * it survives a phone's resolution. Hovering the bar lists which steps are
 * met, so the figure can be checked rather than believed - and a metric can
 * go backwards when harder cards arrive, which is the point of measuring it.
 *
 * **Bias is shown beside the error, signed.** A metric that is out by four
 * points in the same direction every time is a different problem from one
 * that is out by four points at random: the first is a fault with a cause
 * and the second is noise. Averaging them into one figure hides which.
 *
 * **And the error is shown against the trivial answer.** Nearly every
 * graded card is well centred, so a system that always said "perfectly
 * centred" scores 66 to 75 per cent within three points while knowing
 * nothing. Both centering axes were read as working on that basis. The
 * grader's own spread is shown too, because where it is small there is
 * very little for any measurement to explain.
 */
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { CalibrationMetric } from '../../api/client';

const STATE_COLOUR: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  working: 'success',
  'in progress': 'warning',
  started: 'info',
  'not started': 'default',
};

/** The rubric behind a percentage, as a list a person can read. */
function StepList({ steps }: { steps: Record<string, boolean> }) {
  return (
    <Stack spacing={0.25}>
      {Object.entries(steps).map(([name, met]) => (
        <Typography key={name} variant="caption">
          {met ? '✓' : '·'} {name}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * **The headline is skill, not agreement.** Graded cards are nearly all
 * well centred, so "within three points" is cleared by answering
 * "perfectly centred" for every card - both centering axes read as passing
 * on that basis while being, measured against the guess, worse than it. So
 * the error is shown against what guessing would have scored, and the
 * agreement rate sits underneath as context rather than as the verdict.
 */
function Accuracy({ metric, within }: { metric: CalibrationMetric; within: number }) {
  const got = metric.accuracy;
  if (!got) {
    return (
      <Typography variant="body2" color="text.secondary">
        not scored
      </Typography>
    );
  }
  const better = got.mean_error < got.baseline_error;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" color={better ? 'success.main' : 'warning.main'}>
        mean {got.mean_error.toFixed(2)} vs {got.baseline_error.toFixed(2)} guessing
        {got.sound ? '' : ' (thin)'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        r={got.correlation >= 0 ? '+' : ''}
        {got.correlation.toFixed(2)} · bias {got.bias >= 0 ? '+' : ''}
        {got.bias.toFixed(2)} · n={got.scored}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {got.within.toFixed(0)}% within {within} · grader&rsquo;s spread{' '}
        {got.spread.toFixed(2)}
      </Typography>
    </Stack>
  );
}

export function MetricProgressTable({
  metrics,
  within,
}: {
  metrics: CalibrationMetric[];
  within: number;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ pl: 0 }}>Metric</TableCell>
          <TableCell>Outputs</TableCell>
          <TableCell>State</TableCell>
          <TableCell align="right">Against guessing</TableCell>
          <TableCell sx={{ minWidth: 140 }}>Complete</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {metrics.map((metric) => (
          <TableRow key={metric.key}>
            <TableCell sx={{ pl: 0, verticalAlign: 'top' }}>
              <Typography variant="body2">{metric.name}</Typography>
              <Tooltip title={metric.truth}>
                <Typography variant="caption" color="text.secondary">
                  checked against the grader
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top', maxWidth: 260 }}>
              <Typography variant="caption" color="text.secondary">
                {metric.outputs}
              </Typography>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Chip
                size="small"
                label={metric.state}
                color={STATE_COLOUR[metric.state] ?? 'default'}
                variant={metric.state === 'working' ? 'filled' : 'outlined'}
              />
            </TableCell>
            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
              <Accuracy metric={metric} within={within} />
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Tooltip title={<StepList steps={metric.steps} />}>
                <Stack spacing={0.5}>
                  <LinearProgress
                    variant="determinate"
                    value={metric.completeness}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {metric.completeness}%
                  </Typography>
                </Stack>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
